import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { login } from '../../api';
import logoWordmark from '../../assets/logo-wordmark.png';
import styles from './LoginPage.module.css';

export default function LoginPage({ onAuthSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionBanner, setSessionBanner] = useState('');
  const [showForgotMessage, setShowForgotMessage] = useState(false);

  useEffect(() => {
    const reason = sessionStorage.getItem('auth_redirect_reason');
    if (reason === 'session_expired') {
      setSessionBanner('Your session has expired. Please log in again.');
      sessionStorage.removeItem('auth_redirect_reason');
    }
  }, []);

  const navigate = useNavigate();

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError('');
      setShowForgotMessage(false);
      setIsSubmitting(true);

      try {
        const data = await login(username, password);

        // Persist tokens and user to localStorage
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Lift user state to App level
        if (onAuthSuccess) {
          onAuthSuccess(data.user);
        }

        navigate('/dashboard');
      } catch (err) {
        if (err.message === 'Failed to fetch') {
          setError('Unable to connect. Please try again.');
        } else if (err.status === 423) {
          setError('Your account is locked. Please contact your system administrator.');
          setShowForgotMessage(true);
        } else {
          setError('Invalid username or password. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [username, password, navigate, onAuthSuccess]
  );

  return (
    <main className={styles.page}>
      <div className={styles.container}>
        {/* Wordmark */}
        <div className={styles.wordmark}>
          <img
            src={logoWordmark}
            alt="courtesy | point"
            className={styles.wordmarkImg}
          />
        </div>

        {/* Session expired banner */}
        {sessionBanner && (
          <div className={styles.sessionBanner} role="alert">
            {sessionBanner}
          </div>
        )}

        {/* Card */}
        <div className={styles.card}>
          {/* Header */}
          <div className={styles.header}>
            <h1 className={styles.title}>Welcome back</h1>
            <p className={styles.subtitle}>Sign in to Tee Time scheduler</p>
          </div>

          {/* Form */}
          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            {/* Username */}
            <div className={styles.field}>
              <label className={styles.label} htmlFor="username">
                Username
              </label>
              <input
                id="username"
                type="text"
                className={styles.input}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Password */}
            <div className={styles.field}>
              <label className={styles.label} htmlFor="password">
                Password
              </label>
              <div className={styles.passwordWrapper}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`${styles.input} ${styles.passwordInput}`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className={styles.showHideBtn}
                  onClick={toggleShowPassword}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff size={16} strokeWidth={1.5} />
                  ) : (
                    <Eye size={16} strokeWidth={1.5} />
                  )}
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <p className={styles.errorMsg} role="alert">
                {error}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </button>

            {/* Forgot password */}
            <div className={styles.forgotRow}>
              <button
                type="button"
                className={styles.forgotLink}
                onClick={() => setShowForgotMessage((v) => !v)}
              >
                Forgot password?
              </button>
              {showForgotMessage && (
                <p className={styles.forgotMsg} role="status">
                  Contact your system administrator to reset your password.
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className={styles.footer}>
          Trouble signing in? Contact your system administrator.
        </p>
      </div>
    </main>
  );
}
